import React, { useState, useEffect } from 'react';
import { api, API, getToken } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import AuditoriaSection from './AuditoriaSection';
import InformesProgramadosCard from './InformesProgramadosCard';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import {
  FileText, FileSpreadsheet, Search, AlertTriangle, Clock, Coffee, Moon,
  TrendingUp, TrendingDown, Check, CalendarOff, PartyPopper, ShieldCheck,
  BarChart3, Building2, Sparkles, Timer, MoonStar,
} from 'lucide-react';
import { formatHoras, AUSENCIA_LABEL, AUSENCIA_STYLE } from '../../lib/labels';

const hoyISO = () => new Date().toISOString().slice(0, 10);
const restarDias = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const primerDiaMes = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const TIPOS = [
  { id: 'detallado', label: 'Detallado día a día', icon: FileText, desc: 'Registro completo de fichajes por empleado y jornada.' },
  { id: 'resumen_ejecutivo', label: 'Resumen ejecutivo', icon: Sparkles, desc: 'KPIs y rankings para dirección. Ideal para el CEO.' },
  { id: 'horas_extra', label: 'Horas extra', icon: Timer, desc: 'Análisis detallado de horas extra y déficit por empleado.' },
  { id: 'absentismo', label: 'Absentismo', icon: CalendarOff, desc: 'Tasa de absentismo, motivos y ranking.' },
  { id: 'cumplimiento_legal', label: 'Cumplimiento legal', icon: ShieldCheck, desc: 'Descansos, jornadas máximas y firmas (RD-Ley 8/2019).' },
  { id: 'nocturnidad', label: 'Nocturnidad', icon: MoonStar, desc: 'Trabajo nocturno (22:00–06:00). Detecta trabajadores nocturnos legales según art. 36 ET.' },
  { id: 'productividad_depts', label: 'Por departamento', icon: Building2, desc: 'Comparativa de productividad entre departamentos.' },
];

const KpiBox = ({ label, value, tone = 'neutral', testid }) => {
  const tones = {
    neutral: 'bg-zinc-50 border-zinc-200 text-zinc-900',
    green: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    red: 'bg-red-50 border-red-200 text-red-900',
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
  };
  return (
    <div className={`rounded-lg border px-4 py-3 ${tones[tone]}`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-xl font-semibold tabular-nums mt-0.5">{value}</div>
    </div>
  );
};

const ReportesTab = () => {
  const [tipo, setTipo] = useState('detallado');
  const [empleados, setEmpleados] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [username, setUsername] = useState('todos');
  const [departamentoId, setDepartamentoId] = useState('todos');
  const [desde, setDesde] = useState(restarDias(7));
  const [hasta, setHasta] = useState(hoyISO());
  const [reporte, setReporte] = useState(null); // Solo para detallado
  const [analitica, setAnalitica] = useState(null); // Para los avanzados
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/admin/empleados').then((r) => setEmpleados(r.data)).catch(() => {});
    api.get('/admin/departamentos').then((r) => setDepartamentos(r.data)).catch(() => {});
  }, []);

  const generar = async () => {
    setLoading(true);
    setReporte(null); setAnalitica(null);
    try {
      if (tipo === 'detallado') {
        const params = { desde, hasta };
        if (username !== 'todos') params.username = username;
        if (departamentoId !== 'todos') params.departamento_id = departamentoId;
        const res = await api.get('/admin/reportes', { params });
        setReporte(res.data.reporte);
      } else {
        const params = { desde, hasta };
        if (departamentoId !== 'todos') params.departamento_id = departamentoId;
        const res = await api.get(`/admin/informes/analitica/${tipo}`, { params });
        setAnalitica(res.data);
      }
    } catch (err) {
      toast.error('Error al generar el informe');
    } finally {
      setLoading(false);
    }
  };

  const aplicarPreset = (preset) => {
    if (preset === 'hoy') { setDesde(hoyISO()); setHasta(hoyISO()); }
    if (preset === 'semana') { setDesde(restarDias(6)); setHasta(hoyISO()); }
    if (preset === 'mes') { setDesde(primerDiaMes()); setHasta(hoyISO()); }
    if (preset === 'trimestre') { setDesde(restarDias(89)); setHasta(hoyISO()); }
  };

  const exportar = (formato) => {
    const token = getToken();
    const params = new URLSearchParams({ formato, desde, hasta, token });
    if (departamentoId !== 'todos') params.append('departamento_id', departamentoId);
    if (tipo === 'detallado') {
      if (username !== 'todos') params.append('username', username);
      window.open(`${API}/admin/reportes/export?${params.toString()}`, '_blank');
    } else {
      window.open(`${API}/admin/informes/export/${tipo}?${params.toString()}`, '_blank');
    }
  };

  const tipoActual = TIPOS.find((t) => t.id === tipo) || TIPOS[0];
  const IconoTipo = tipoActual.icon;

  return (
    <div className="space-y-6" data-testid="reportes-tab">
      {/* Selector de tipo */}
      <Card className="border-zinc-200">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <BarChart3 className="h-5 w-5" /> Suite de informes profesionales
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-2">
            {TIPOS.map((t) => {
              const Icon = t.icon;
              const on = tipo === t.id;
              return (
                <button key={t.id} type="button" onClick={() => { setTipo(t.id); setAnalitica(null); setReporte(null); }}
                  data-testid={`tipo-informe-${t.id}`}
                  className={`text-left p-3 rounded-lg border transition-all ${on ? 'border-zinc-900 bg-zinc-900 text-white shadow-sm' : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400'}`}>
                  <Icon className={`h-4 w-4 mb-1.5 ${on ? 'text-white' : 'text-zinc-500'}`} />
                  <div className="text-xs font-medium leading-tight">{t.label}</div>
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex items-start gap-2 text-xs text-zinc-500">
            <IconoTipo className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>{tipoActual.desc}</span>
          </div>
        </CardContent>
      </Card>

      {/* Filtros */}
      <Card className="border-zinc-200">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Filtros del informe</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4 items-end">
            {tipo === 'detallado' && (
              <div className="space-y-1.5">
                <Label>Empleado</Label>
                <Select value={username} onValueChange={setUsername}>
                  <SelectTrigger className="w-[220px]" data-testid="report-employee-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos los empleados</SelectItem>
                    {empleados.map((e) => (
                      <SelectItem key={e.username} value={e.username}>{e.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Departamento</Label>
              <Select value={departamentoId} onValueChange={setDepartamentoId}>
                <SelectTrigger className="w-[200px]" data-testid="report-departamento-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los departamentos</SelectItem>
                  {departamentos.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Desde</Label>
              <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
                className="w-[160px]" data-testid="report-desde" />
            </div>
            <div className="space-y-1.5">
              <Label>Hasta</Label>
              <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
                className="w-[160px]" data-testid="report-hasta" />
            </div>
            <Button onClick={generar} disabled={loading} className="bg-zinc-900 hover:bg-zinc-800" data-testid="generar-reporte-btn">
              <Search className="h-4 w-4 mr-2" /> {loading ? 'Generando...' : 'Generar'}
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => aplicarPreset('hoy')} data-testid="preset-hoy">Hoy</Button>
            <Button size="sm" variant="outline" onClick={() => aplicarPreset('semana')} data-testid="preset-semana">Última semana</Button>
            <Button size="sm" variant="outline" onClick={() => aplicarPreset('mes')} data-testid="preset-mes">Este mes</Button>
            <Button size="sm" variant="outline" onClick={() => aplicarPreset('trimestre')} data-testid="preset-trimestre">Últimos 90d</Button>
            <div className="flex-1" />
            <Button size="sm" variant="outline" onClick={() => exportar('pdf')} data-testid="export-pdf-btn">
              <FileText className="h-4 w-4 mr-2" /> Exportar PDF
            </Button>
            <Button size="sm" variant="outline" onClick={() => exportar('excel')} data-testid="export-excel-btn">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Exportar Excel
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Resultado — DETALLADO */}
      {tipo === 'detallado' && reporte && reporte.length === 0 && (
        <Card className="border-zinc-200"><CardContent className="py-10 text-center text-zinc-400">
          No hay fichajes para el periodo seleccionado.
        </CardContent></Card>
      )}

      {tipo === 'detallado' && reporte && reporte.map((emp) => (
        <Card key={emp.username} className="border-zinc-200" data-testid={`report-emp-${emp.username}`}>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle className="font-heading text-lg">{emp.full_name}</CardTitle>
              <div className="flex flex-wrap gap-2 text-sm">
                <Badge variant="outline" className="gap-1"><Clock className="h-3.5 w-3.5" /> {formatHoras(emp.total_horas)} trabajadas</Badge>
                <Badge variant="outline" className="gap-1"><Moon className="h-3.5 w-3.5" /> {formatHoras(emp.total_nocturnas)} nocturnas</Badge>
                <Badge variant="outline" className="gap-1"><Coffee className="h-3.5 w-3.5" /> {formatHoras(emp.total_descanso)} descanso</Badge>
                {Math.abs(emp.total_desvio) < 0.01 ? (
                  <Badge className="gap-1 bg-zinc-100 text-zinc-700">
                    <Check className="h-3.5 w-3.5" /> A la hora
                  </Badge>
                ) : (
                  <Badge className={`gap-1 ${emp.total_desvio > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {emp.total_desvio > 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                    {formatHoras(emp.total_desvio, true)} {emp.total_desvio > 0 ? 'extra' : 'déficit'}
                  </Badge>
                )}
                <Badge variant="outline">{emp.dias_trabajados} días</Badge>
                {emp.total_incidencias > 0 && (
                  <Badge className="bg-amber-100 text-amber-800 gap-1"><AlertTriangle className="h-3.5 w-3.5" /> {emp.total_incidencias} incidencias</Badge>
                )}
                {emp.ausencias && Object.entries(emp.ausencias).map(([t, dias]) => (
                  <Badge key={t} className={`gap-1 ${AUSENCIA_STYLE[t] || 'bg-zinc-100 text-zinc-700'}`} data-testid={`report-ausencia-${emp.username}-${t}`}>
                    <CalendarOff className="h-3.5 w-3.5" /> {AUSENCIA_LABEL[t] || t}: {dias} {dias === 1 ? 'día' : 'días'}
                  </Badge>
                ))}
              </div>
            </div>
            {emp.ausencias_detalle && emp.ausencias_detalle.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-3 mt-1 border-t border-zinc-100" data-testid={`report-ausencias-detalle-${emp.username}`}>
                {emp.ausencias_detalle.map((a, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-full px-2.5 py-1">
                    <span className={`w-1.5 h-1.5 rounded-full ${(AUSENCIA_STYLE[a.tipo] || '').split(' ')[0].replace('bg-', 'bg-').replace('-100', '-500')}`} />
                    {AUSENCIA_LABEL[a.tipo] || a.tipo}: {new Date(a.fecha_inicio).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Madrid' })}–{new Date(a.fecha_fin).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Madrid' })}
                    {a.motivo ? ` (${a.motivo})` : ''}
                  </span>
                ))}
              </div>
            )}
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-50">
                  <tr className="text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                    <th className="py-2.5 px-5">Fecha</th>
                    <th className="py-2.5 px-5">Horario</th>
                    <th className="py-2.5 px-5">Entrada</th>
                    <th className="py-2.5 px-5">Salida</th>
                    <th className="py-2.5 px-5">Descanso</th>
                    <th className="py-2.5 px-5">Horas trab.</th>
                    <th className="py-2.5 px-5">Nocturnas</th>
                    <th className="py-2.5 px-5">Desvío</th>
                    <th className="py-2.5 px-5">Incidencias</th>
                  </tr>
                </thead>
                <tbody>
                  {emp.dias.map((d, i) => (
                    <tr key={i} className={`border-t border-zinc-100 hover:bg-zinc-50 ${d.festivo ? 'bg-red-50/40' : ''}`}>
                      <td className="py-2.5 px-5 tabular-nums">
                        <div>{new Date(d.fecha).toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid' })}</div>
                        {d.festivo && (
                          <Badge className="mt-1 bg-red-100 text-red-700 gap-1" data-testid={`report-festivo-${emp.username}-${i}`}>
                            <PartyPopper className="h-3 w-3" /> {d.festivo}
                          </Badge>
                        )}
                      </td>
                      <td className="py-2.5 px-5 capitalize">{d.horario}</td>
                      <td className="py-2.5 px-5 tabular-nums">{d.entrada ? new Date(d.entrada).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' }) : '-'}</td>
                      <td className="py-2.5 px-5 tabular-nums">{d.salida ? new Date(d.salida).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' }) : '-'}</td>
                      <td className="py-2.5 px-5 tabular-nums text-amber-600">{formatHoras(d.tiempo_descansos)}</td>
                      <td className="py-2.5 px-5 tabular-nums font-semibold text-green-700">{formatHoras(d.horas_trabajadas)}</td>
                      <td className="py-2.5 px-5 tabular-nums text-indigo-600">{formatHoras(d.horas_nocturnas)}</td>
                      <td className={`py-2.5 px-5 tabular-nums font-medium ${!d.completo ? 'text-zinc-300' : d.desvio >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {d.completo ? formatHoras(d.desvio, true) : '—'}
                      </td>
                      <td className="py-2.5 px-5">
                        {d.incidencias.length === 0
                          ? <span className="text-zinc-300">—</span>
                          : d.incidencias.map((inc, j) => (
                            <Badge key={j} className="bg-amber-100 text-amber-800 mr-1 mb-1">{inc}</Badge>
                          ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Resultado — RESUMEN EJECUTIVO */}
      {tipo === 'resumen_ejecutivo' && analitica && analitica.kpis && (
        <Card className="border-zinc-200" data-testid="analitica-resumen">
          <CardHeader><CardTitle className="font-heading text-lg">Resumen ejecutivo</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
              <KpiBox label="Empleados" value={analitica.kpis.empleados_activos} tone="neutral" testid="kpi-empleados" />
              <KpiBox label="H. Trabajadas" value={formatHoras(analitica.kpis.horas_trabajadas)} tone="blue" testid="kpi-trabajadas" />
              <KpiBox label="H. Esperadas" value={formatHoras(analitica.kpis.horas_esperadas)} tone="neutral" testid="kpi-esperadas" />
              <KpiBox label="H. Extra" value={formatHoras(analitica.kpis.horas_extra, true)} tone="green" testid="kpi-extras" />
              <KpiBox label="Déficit" value={formatHoras(analitica.kpis.deficit, true)} tone="red" testid="kpi-deficit" />
              <KpiBox label="H. Nocturnas" value={formatHoras(analitica.kpis.horas_nocturnas)} tone="blue" testid="kpi-nocturnas" />
              <KpiBox label="Incidencias" value={analitica.kpis.incidencias} tone="amber" testid="kpi-incidencias" />
              <KpiBox label="Días trab." value={analitica.kpis.dias_trabajados} tone="neutral" testid="kpi-diastrab" />
              <KpiBox label="Ausencias" value={analitica.kpis.dias_ausencia} tone="amber" testid="kpi-ausencias" />
              <KpiBox label="Absentismo" value={`${analitica.kpis.tasa_absentismo_pct}%`} tone="amber" testid="kpi-absentismo" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { titulo: 'Top horas extra', items: analitica.ranking_extras, formato: 'h', color: 'green' },
                { titulo: 'Top déficit', items: analitica.ranking_deficit, formato: 'h', color: 'red' },
                { titulo: 'Top incidencias', items: analitica.ranking_incidencias, formato: 'n', color: 'amber' },
              ].map((b) => (
                <div key={b.titulo} className="rounded-lg border border-zinc-200 p-3">
                  <div className="text-sm font-semibold mb-2">{b.titulo}</div>
                  {b.items.length === 0 && <div className="text-xs text-zinc-400">Sin datos.</div>}
                  <ol className="space-y-1.5">
                    {b.items.map(([nombre, val], i) => (
                      <li key={i} className="flex items-center justify-between text-sm">
                        <span className="text-zinc-700 truncate">{i + 1}. {nombre}</span>
                        <span className={`font-semibold tabular-nums text-${b.color}-700`}>
                          {b.formato === 'h' ? formatHoras(val, true) : val}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resultado — HORAS EXTRA */}
      {tipo === 'horas_extra' && analitica && analitica.filas && (
        <Card className="border-zinc-200" data-testid="analitica-horas-extra">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Análisis de horas extra</CardTitle>
            <div className="flex flex-wrap gap-2 pt-2">
              <Badge className="bg-green-100 text-green-800 gap-1">Total extras: {formatHoras(analitica.totales.extras, true)}</Badge>
              <Badge className="bg-red-100 text-red-800 gap-1">Total déficit: {formatHoras(analitica.totales.deficit, true)}</Badge>
              <Badge variant="outline">{analitica.totales.empleados} empleados</Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="py-2.5 px-5">Empleado</th>
                    <th className="py-2.5 px-5">Esperadas</th>
                    <th className="py-2.5 px-5">Trabajadas</th>
                    <th className="py-2.5 px-5">Extras</th>
                    <th className="py-2.5 px-5">Déficit</th>
                    <th className="py-2.5 px-5">Balance</th>
                    <th className="py-2.5 px-5">Días extra</th>
                    <th className="py-2.5 px-5">Días déficit</th>
                    <th className="py-2.5 px-5">Nocturnas</th>
                  </tr>
                </thead>
                <tbody>
                  {analitica.filas.length === 0 && (
                    <tr><td colSpan={9} className="py-8 text-center text-zinc-400">Sin datos.</td></tr>
                  )}
                  {analitica.filas.map((f, i) => (
                    <tr key={i} className="border-t border-zinc-100 hover:bg-zinc-50">
                      <td className="py-2.5 px-5 font-medium">{f.empleado}</td>
                      <td className="py-2.5 px-5 tabular-nums">{formatHoras(f.horas_esperadas)}</td>
                      <td className="py-2.5 px-5 tabular-nums">{formatHoras(f.horas_trabajadas)}</td>
                      <td className="py-2.5 px-5 tabular-nums text-green-700 font-semibold">{formatHoras(f.horas_extra, true)}</td>
                      <td className="py-2.5 px-5 tabular-nums text-red-700 font-semibold">{formatHoras(f.deficit, true)}</td>
                      <td className={`py-2.5 px-5 tabular-nums font-semibold ${f.balance >= 0 ? 'text-green-700' : 'text-red-700'}`}>{formatHoras(f.balance, true)}</td>
                      <td className="py-2.5 px-5 tabular-nums">{f.dias_con_extra}</td>
                      <td className="py-2.5 px-5 tabular-nums">{f.dias_con_deficit}</td>
                      <td className="py-2.5 px-5 tabular-nums text-indigo-600">{formatHoras(f.nocturnas)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resultado — ABSENTISMO */}
      {tipo === 'absentismo' && analitica && analitica.filas && analitica.totales && (
        <Card className="border-zinc-200" data-testid="analitica-absentismo">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Análisis de absentismo</CardTitle>
            <div className="flex flex-wrap gap-2 pt-2">
              <Badge className="bg-amber-100 text-amber-800">Días ausencia: {analitica.totales.dias_ausencia}</Badge>
              <Badge className="bg-amber-100 text-amber-800">Tasa global: {analitica.totales.tasa_pct_global}%</Badge>
              {Object.entries(analitica.totales.por_tipo).map(([t, d]) => (
                <Badge key={t} className={AUSENCIA_STYLE[t] || 'bg-zinc-100 text-zinc-700'}>
                  {AUSENCIA_LABEL[t] || t}: {d}
                </Badge>
              ))}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="py-2.5 px-5">Empleado</th>
                    <th className="py-2.5 px-5">Días trabajados</th>
                    <th className="py-2.5 px-5">Días ausencia</th>
                    <th className="py-2.5 px-5">Tasa %</th>
                    <th className="py-2.5 px-5">Desglose</th>
                  </tr>
                </thead>
                <tbody>
                  {analitica.filas.length === 0 && (
                    <tr><td colSpan={5} className="py-8 text-center text-zinc-400">Sin datos.</td></tr>
                  )}
                  {analitica.filas.map((f, i) => (
                    <tr key={i} className="border-t border-zinc-100 hover:bg-zinc-50">
                      <td className="py-2.5 px-5 font-medium">{f.empleado}</td>
                      <td className="py-2.5 px-5 tabular-nums">{f.dias_trabajados}</td>
                      <td className="py-2.5 px-5 tabular-nums">{f.dias_ausencia}</td>
                      <td className={`py-2.5 px-5 tabular-nums font-semibold ${f.tasa_pct > 10 ? 'text-red-600' : 'text-zinc-700'}`}>{f.tasa_pct}%</td>
                      <td className="py-2.5 px-5">
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(f.desglose).length === 0 && <span className="text-xs text-zinc-300">—</span>}
                          {Object.entries(f.desglose).map(([t, d]) => (
                            <Badge key={t} className={`text-xs ${AUSENCIA_STYLE[t] || 'bg-zinc-100 text-zinc-700'}`}>
                              {AUSENCIA_LABEL[t] || t}: {d}
                            </Badge>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resultado — CUMPLIMIENTO LEGAL */}
      {tipo === 'cumplimiento_legal' && analitica && analitica.filas && (
        <Card className="border-zinc-200" data-testid="analitica-cumplimiento">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-700" /> Cumplimiento legal
            </CardTitle>
            <div className="flex flex-wrap gap-2 pt-2">
              <Badge className={analitica.total_alertas === 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}>
                {analitica.total_alertas === 0 ? '✓ Sin alertas' : `${analitica.total_alertas} alertas detectadas`}
              </Badge>
            </div>
            <p className="text-xs text-zinc-500 pt-1">
              Basado en RD-Ley 8/2019 y Estatuto de los Trabajadores (art. 34-38): descanso mínimo entre jornadas (12h),
              jornada diaria máxima (9h), fichajes sin firma de fin y horas nocturnas &gt; 8h/día.
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="py-2.5 px-5">Empleado</th>
                    <th className="py-2.5 px-5">Descanso &lt;12h</th>
                    <th className="py-2.5 px-5">Jornada &gt;9h</th>
                    <th className="py-2.5 px-5">Sin firma fin</th>
                    <th className="py-2.5 px-5">Nocturnas &gt;8h</th>
                    <th className="py-2.5 px-5">Otras</th>
                    <th className="py-2.5 px-5 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {analitica.filas.length === 0 && (
                    <tr><td colSpan={7} className="py-8 text-center text-zinc-400">Sin datos.</td></tr>
                  )}
                  {analitica.filas.map((f, i) => (
                    <tr key={i} className="border-t border-zinc-100 hover:bg-zinc-50">
                      <td className="py-2.5 px-5 font-medium">{f.empleado}</td>
                      <td className="py-2.5 px-5 tabular-nums">{f.alertas.descanso_12h || '—'}</td>
                      <td className="py-2.5 px-5 tabular-nums">{f.alertas.jornada_9h_superada || '—'}</td>
                      <td className="py-2.5 px-5 tabular-nums">{f.alertas.sin_firma_fin || '—'}</td>
                      <td className="py-2.5 px-5 tabular-nums">{f.alertas.nocturnas_exceso || '—'}</td>
                      <td className="py-2.5 px-5 tabular-nums">{f.alertas.otras || '—'}</td>
                      <td className={`py-2.5 px-5 text-right font-semibold ${f.total === 0 ? 'text-emerald-700' : 'text-red-700'}`}>{f.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resultado — NOCTURNIDAD */}
      {tipo === 'nocturnidad' && analitica && analitica.filas && analitica.kpis && (
        <Card className="border-zinc-200" data-testid="analitica-nocturnidad">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <MoonStar className="h-5 w-5 text-indigo-700" /> Informe de Nocturnidad
            </CardTitle>
            <p className="text-xs text-zinc-500 pt-1">
              Franja nocturna aplicada: <b>22:00 – 06:00</b>. Se considera trabajador nocturno legal quien realiza
              habitualmente más de 3h nocturnas por jornada o cuya jornada nocturna supera 1/3 del total (art. 36 ET).
              Máximo legal: media 8h/día nocturnas en cualquier ventana de 15 días consecutivos.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <KpiBox label="H. Nocturnas" value={formatHoras(analitica.kpis.horas_nocturnas_total)} tone="blue" testid="kpi-noct-total" />
              <KpiBox label="Empleados con Noct." value={analitica.kpis.empleados_con_nocturnas} tone="neutral" testid="kpi-noct-emp" />
              <KpiBox label="Noches trabajadas" value={analitica.kpis.noches_trabajadas_total} tone="neutral" testid="kpi-noct-noches" />
              <KpiBox label="Media h/noche" value={formatHoras(analitica.kpis.media_h_por_noche_global)} tone="blue" testid="kpi-noct-media" />
              <KpiBox label="Trab. nocturnos" value={analitica.kpis.trabajadores_nocturnos}
                tone={analitica.kpis.trabajadores_nocturnos > 0 ? 'amber' : 'neutral'} testid="kpi-noct-trab" />
              <KpiBox label="Alertas &gt;8h/15d" value={analitica.kpis.alertas_media_15d}
                tone={analitica.kpis.alertas_media_15d > 0 ? 'red' : 'green'} testid="kpi-noct-alertas" />
            </div>
          </CardContent>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="py-2.5 px-5">Empleado</th>
                    <th className="py-2.5 px-5">H. Nocturnas</th>
                    <th className="py-2.5 px-5">Noches</th>
                    <th className="py-2.5 px-5">Media h/noche</th>
                    <th className="py-2.5 px-5">% Noct.</th>
                    <th className="py-2.5 px-5">Pico 15d</th>
                    <th className="py-2.5 px-5">Trab. nocturno</th>
                    <th className="py-2.5 px-5">Alerta &gt;8h/15d</th>
                  </tr>
                </thead>
                <tbody>
                  {analitica.filas.length === 0 && (
                    <tr><td colSpan={8} className="py-8 text-center text-zinc-400">Sin datos.</td></tr>
                  )}
                  {analitica.filas.map((f, i) => (
                    <tr key={i} className="border-t border-zinc-100 hover:bg-zinc-50">
                      <td className="py-2.5 px-5 font-medium">{f.empleado}</td>
                      <td className="py-2.5 px-5 tabular-nums text-indigo-700 font-semibold">{formatHoras(f.horas_nocturnas)}</td>
                      <td className="py-2.5 px-5 tabular-nums">{f.noches_trabajadas}</td>
                      <td className="py-2.5 px-5 tabular-nums">{formatHoras(f.media_h_por_noche)}</td>
                      <td className="py-2.5 px-5 tabular-nums">{f.pct_nocturnas}%</td>
                      <td className={`py-2.5 px-5 tabular-nums ${f.pico_media_15d > 8 ? 'text-red-600 font-semibold' : ''}`}>
                        {formatHoras(f.pico_media_15d)}
                      </td>
                      <td className="py-2.5 px-5">
                        {f.es_trabajador_nocturno
                          ? <Badge className="bg-amber-100 text-amber-800 gap-1"><Moon className="h-3.5 w-3.5" /> Sí</Badge>
                          : <span className="text-zinc-300">—</span>}
                      </td>
                      <td className="py-2.5 px-5">
                        {f.alerta_media_15d
                          ? <Badge className="bg-red-100 text-red-800 gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Exceso</Badge>
                          : <span className="text-zinc-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resultado — PRODUCTIVIDAD DEPARTAMENTOS */}
      {tipo === 'productividad_depts' && analitica && analitica.filas && (
        <Card className="border-zinc-200" data-testid="analitica-productividad">
          <CardHeader><CardTitle className="font-heading text-lg">Productividad por departamento</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="py-2.5 px-5">Departamento</th>
                    <th className="py-2.5 px-5">Empleados</th>
                    <th className="py-2.5 px-5">Trabajadas</th>
                    <th className="py-2.5 px-5">Esperadas</th>
                    <th className="py-2.5 px-5">Cumplimiento</th>
                    <th className="py-2.5 px-5">H. Extra</th>
                    <th className="py-2.5 px-5">Déficit</th>
                    <th className="py-2.5 px-5">Días ausencia</th>
                    <th className="py-2.5 px-5">Incidencias</th>
                  </tr>
                </thead>
                <tbody>
                  {analitica.filas.length === 0 && (
                    <tr><td colSpan={9} className="py-8 text-center text-zinc-400">Sin datos.</td></tr>
                  )}
                  {analitica.filas.map((f, i) => (
                    <tr key={i} className="border-t border-zinc-100 hover:bg-zinc-50">
                      <td className="py-2.5 px-5 font-medium">{f.departamento}</td>
                      <td className="py-2.5 px-5 tabular-nums">{f.empleados}</td>
                      <td className="py-2.5 px-5 tabular-nums">{formatHoras(f.horas_trabajadas)}</td>
                      <td className="py-2.5 px-5 tabular-nums">{formatHoras(f.horas_esperadas)}</td>
                      <td className={`py-2.5 px-5 tabular-nums font-semibold ${f.cumplimiento_pct >= 100 ? 'text-emerald-700' : 'text-red-700'}`}>
                        {f.cumplimiento_pct}%
                      </td>
                      <td className="py-2.5 px-5 tabular-nums text-green-700">{formatHoras(f.horas_extra, true)}</td>
                      <td className="py-2.5 px-5 tabular-nums text-red-700">{formatHoras(f.deficit, true)}</td>
                      <td className="py-2.5 px-5 tabular-nums">{f.dias_ausencia}</td>
                      <td className="py-2.5 px-5 tabular-nums">{f.incidencias}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Informes programados (envío automático por email) */}
      <InformesProgramadosCard />

      <AuditoriaSection />
    </div>
  );
};

export default ReportesTab;
